/**
 * Browser push for this device: support detection, permission, subscribe and unsubscribe, and the user's topics.
 *
 * ONE module-level state however many components read it. Permission is requested only from `enable()`, which the card
 * calls from a button press: Firefox and Safari refuse a prompt without a user gesture and Chrome punishes one on page load.
 * `load()` never prompts. A device is "on" only when the browser holds a subscription AND the server confirmed it; a failure
 * at any step leaves the state at what is true, never at "on".
 */
import { computed, reactive } from 'vue'
import { toast } from 'vue-sonner'
import { resolveCsrfToken } from '@/utils/csrf'
import {
  LOGOUT_UNSUBSCRIBE_TIMEOUT_MS,
  PUSH_API_BASE,
  HEAL_MAX_ATTEMPTS,
  HEAL_RETRY_BASE_MS,
  PUSH_OPEN_PATH,
  PUSH_TOPICS_SAVE_FAILED,
  SW_MESSAGE_OPEN_NOTIFICATIONS,
  SW_MESSAGE_SUBSCRIPTION_CHANGED,
  SW_READY_TIMEOUT_MS,
} from '@/utils/webPushConfig'

// The endpoint this page load has already registered with the server. A header remounts on every Inertia visit, so
// without this each navigation would POST again (and a POST resets the server's failure count for the row).
let registeredEndpoint = null
let initialHealDone = false
let healTimer = null

const state = reactive({
  status: 'loading', // loading | forbidden | unsupported | needsHomeScreen | disabled | misconfigured | denied | off | on | failed
  topics: [],
  availableTopics: [],
  busy: false,
  errorStatus: null, // the HTTP status of the request that put the device in 'failed' (null: no answer, or not failed)
})

/** A step failed: say which and keep the HTTP status in the state. The caller decides the status (a silent heal must not). */
function recordFailure(operation, error) {
  console.error('Web push step failed', { operation, error, status: error?.status ?? null })
  state.errorStatus = error?.status ?? null
}

export function supportsPush() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** iOS and iPadOS deliver push only to a web app added to the Home Screen (standalone). */
export function needsHomeScreen() {
  const ua = navigator.userAgent || ''
  const iOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1)
  const standalone =
    navigator.standalone === true ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  return iOS && !standalone
}

function urlBase64ToUint8Array(base64) {
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

async function api(method, path, body, { keepalive = false } = {}) {
  const csrf = method === 'GET' ? null : resolveCsrfToken()
  const response = await fetch(`${PUSH_API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(csrf ? { 'X-XSRF-TOKEN': csrf } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    ...(keepalive ? { keepalive: true } : {}),
  })
  if (!response || !response.ok) {
    const error = new Error(`HTTP ${response ? response.status : 'none'}`)
    error.status = response ? response.status : null
    throw error
  }
  return response.json()
}

async function currentSubscription() {
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return { registration: null, subscription: null }
  return { registration, subscription: await registration.pushManager.getSubscription() }
}

/** `ready` resolves only once a worker is active: when registration failed it never does, so the wait is bounded. */
async function serviceWorkerReady() {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('service worker not ready')), SW_READY_TIMEOUT_MS)
  })
  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout])
  } finally {
    clearTimeout(timer)
  }
}

/** Read the server's answer and this browser's own state; never prompts. */
export async function load() {
  state.errorStatus = null
  state.status = await readDeviceState()
}

/** The status this device is in, from the server's answer and the browser's own state. Writes the topics, never the status. */
async function readDeviceState() {
  if (!supportsPush()) return needsHomeScreen() ? 'needsHomeScreen' : 'unsupported'
  if (needsHomeScreen()) return 'needsHomeScreen'
  let config
  try {
    config = await api('GET', '/config')
  } catch (error) {
    // The push API is for admin roles, like the alert API: anyone else has no notification card at all.
    if (error.status === 403) return 'forbidden'
    recordFailure('webPushConfig', error)
    return 'failed'
  }
  state.topics = config.topics || []
  state.availableTopics = config.available_topics || []
  if (config.state === 'DISABLED') {
    return 'disabled'
  }
  if (config.state === 'MISCONFIGURED') {
    return 'misconfigured'
  }
  if (Notification.permission === 'denied') {
    return 'denied'
  }
  const { subscription } = await currentSubscription()
  if (subscription && Notification.permission === 'granted') {
    // Re-register: idempotent on the server, and it heals a subscription the browser rotated or the server pruned. Once
    // per endpoint per page load: a rotation gives a new endpoint and registers again.
    try {
      if (registeredEndpoint !== subscription.endpoint) {
        await api('POST', '/subscriptions', subscription.toJSON())
        registeredEndpoint = subscription.endpoint
      }
      return 'on'
    } catch (error) {
      recordFailure('webPushRegister', error)
      return 'failed'
    }
  }
  return 'off'
}

/** Ask for permission and subscribe. Call it only from a user gesture. */
export async function enable() {
  state.busy = true
  try {
    const config = await api('GET', '/config')
    if (config.state !== 'ENABLED') {
      state.status = config.state === 'MISCONFIGURED' ? 'misconfigured' : 'disabled'
      return
    }
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      state.status = permission === 'denied' ? 'denied' : 'off'
      return
    }
    const registration = await serviceWorkerReady()
    const subscription =
      (await registration.pushManager.getSubscription()) ||
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key),
      }))
    const stored = await api('POST', '/subscriptions', subscription.toJSON())
    registeredEndpoint = subscription.endpoint
    state.topics = stored.topics || []
    state.availableTopics = config.available_topics || []
    state.errorStatus = null
    state.status = 'on'
  } catch (error) {
    recordFailure('webPushEnable', error)
    state.status = 'failed'
  } finally {
    state.busy = false
  }
}

/** Forget this device on the server first, then in the browser; the status follows what actually happened. */
export async function disable() {
  state.busy = true
  try {
    const { subscription } = await currentSubscription()
    if (subscription) {
      try {
        await api('DELETE', '/subscriptions', { endpoint: subscription.endpoint })
      } catch (error) {
        // 404: the server already forgot this browser (pruned); the browser still has to let go of it.
        if (error.status !== 404) throw error
      }
      if (!(await subscription.unsubscribe())) {
        // The browser refused to let go: the device is still subscribed, so it is not 'off'.
        throw new Error('the browser kept its push subscription')
      }
    }
    registeredEndpoint = null
    state.errorStatus = null
    state.status = 'off'
  } catch (error) {
    recordFailure('webPushDisable', error)
    state.status = 'failed'
  } finally {
    state.busy = false
  }
}

/**
 * Before leaving for /logout: ask the server to forget this browser, then drop the browser's own subscription, so the next
 * person to sign in here is not sent this user's alerts. Turning the device off at logout is the design; the next login
 * turns it on again from the Profile card. The browser's subscription is dropped EVEN WHEN the server did not confirm the
 * delete (that is logged): a browser without a subscription receives nothing, and the orphaned server row's endpoint then
 * answers 404 or 410 and the sender deletes it. Bounded: a slow server never holds the logout. The request is `keepalive`, so
 * leaving the page does not cancel it. Never rejects: whatever fails is logged and the logout goes on. A session that expires,
 * or a sign-out from another tab, never runs this; the backstop is `add_subscription`, which moves a browser's subscription
 * to whoever of the same client signs in next on it.
 */
export async function unsubscribeThisDeviceBeforeLogout() {
  if (!supportsPush()) return
  const attempt = (async () => {
    const { subscription } = await currentSubscription()
    if (!subscription) return
    try {
      await api(
        'DELETE',
        '/subscriptions',
        { endpoint: subscription.endpoint },
        { keepalive: true }
      )
    } catch (error) {
      console.warn('Web push: the server did not confirm forgetting this device at logout', {
        operation: 'logoutDeleteSubscription',
        status: error?.status ?? null,
        error,
      })
    }
    if (!(await subscription.unsubscribe())) {
      console.warn('Web push: the browser kept its subscription at logout', {
        operation: 'logoutUnsubscribe',
      })
    }
  })().catch((error) => {
    console.warn('Web push: unsubscribing this device at logout failed; logging out anyway', {
      operation: 'logoutUnsubscribe',
      error,
    })
  })
  await Promise.race([
    attempt,
    new Promise((resolve) => setTimeout(resolve, LOGOUT_UNSUBSCRIBE_TIMEOUT_MS)),
  ])
}

/** True when the choices were saved. A failure leaves the device "on" and the choices as they were, and says so. */
export async function saveTopics(topics) {
  state.busy = true
  try {
    const saved = await api('PUT', '/topics', { topics })
    state.topics = saved.topics
    return true
  } catch {
    toast.error(PUSH_TOPICS_SAVE_FAILED)
    return false
  } finally {
    state.busy = false
  }
}

/** The worker tells every open tab when the browser rotated this device's subscription. */
export function listenForSubscriptionChanges() {
  if (!supportsPush()) return () => {}
  const handler = (event) => {
    if (event.data && event.data.type === SW_MESSAGE_SUBSCRIPTION_CHANGED) load()
  }
  navigator.serviceWorker.addEventListener('message', handler)
  return () => navigator.serviceWorker.removeEventListener('message', handler)
}

/**
 * Holds notification-click visits until the Inertia app has its first page: `router.visit` reads the current page and
 * throws before that. `release()` runs the held visit (the last click wins) and lets later ones through at once. A visit
 * that throws still navigates, by a plain page load, and is logged.
 */
export function createNotificationVisitGate(visit) {
  let released = false
  let held = null
  const go = (url) => {
    try {
      visit(url)
    } catch (error) {
      console.warn('Notification click could not use the router; loading the page instead', {
        operation: 'notificationVisit',
        url,
        error,
      })
      window.location.assign(url)
    }
  }
  const open = () => {
    released = true
    if (held !== null) go(held)
    held = null
  }
  return {
    visit(url) {
      if (released) go(url)
      else held = url
    },
    release: open,
    /** The app will never mount: the held click (and later ones) load the page directly, and the failure is logged. */
    fail(error) {
      console.error(
        'Inertia app failed to initialize; notification clicks load the page directly',
        {
          operation: 'inertiaBoot',
          error,
        }
      )
      open()
    },
  }
}

/**
 * Starts the Inertia app and tells the gate how it went: released after the app's setup mounted it, failed when startup
 * rejected. `createApp` is `createInertiaApp`; `options.setup` is the app's own mount.
 */
export function bootWithVisitGate(createApp, gate, options) {
  return createApp({
    ...options,
    setup(context) {
      const mounted = options.setup(context)
      gate.release()
      return mounted
    },
  }).catch((error) => gate.fail(error))
}

/**
 * Opens /notifications when the service worker asks (a notification click). Started once from main.js, so EVERY page
 * listens, the login page included. Never prompts and touches no subscription. A request for anything else is dropped
 * with a WARNING, so a click that went nowhere leaves a trace (a frozen tab cannot log).
 */
export function listenForNotificationOpen(visit) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {}
  const onOpen = (event) => {
    const data = event.data
    if (!data || data.type !== SW_MESSAGE_OPEN_NOTIFICATIONS) return
    try {
      const target = new URL(data.url, window.location.origin)
      if (target.origin === window.location.origin && target.pathname === PUSH_OPEN_PATH) {
        visit(target.pathname + target.search)
        return
      }
    } catch {
      // falls through to the warning
    }
    console.warn('Notification click asked for a page that is not allowed; ignored', {
      operation: 'listenForNotificationOpen',
      url: data.url,
    })
  }
  navigator.serviceWorker.addEventListener('message', onOpen)
  return () => navigator.serviceWorker.removeEventListener('message', onOpen)
}

/**
 * Heal this device once per page load, retrying a failure at most HEAL_MAX_ATTEMPTS times in all, doubling the wait. A
 * failure here is silent: the person did nothing, so it never touches `state.status`, which the Profile card turns into
 * "could not change the setting". It is logged instead.
 */
function healWithRetry(attempt = 0) {
  readDeviceState()
    .catch((error) => {
      console.warn('Web push heal attempt failed', {
        operation: 'healWithRetry',
        attempt: attempt + 1,
        status: error?.status ?? null,
        error,
      })
      return 'failed'
    })
    .then((status) => {
      if (status !== 'failed') {
        state.status = status
        return
      }
      if (attempt + 1 < HEAL_MAX_ATTEMPTS) {
        healTimer = setTimeout(() => healWithRetry(attempt + 1), HEAL_RETRY_BASE_MS * 2 ** attempt)
      } else {
        console.warn('Web push could not re-register this device', {
          operation: 'healWithRetry',
          attempts: HEAL_MAX_ATTEMPTS,
        })
      }
    })
}

/**
 * Kept alive by the header on every admin page, not by the Profile card: a subscription the browser rotated (the worker
 * re-subscribes and tells the tabs) is re-registered with the server wherever the person happens to be. The first mount of
 * a page load re-registers a granted device, retrying a failure with backoff up to a bound; later mounts only listen. A
 * subscription the server pruned mid-session is re-registered on the next page load or rotation, not before. Permission is
 * never requested here; a device that never turned notifications on is not touched.
 */
export function keepDeviceRegistered() {
  if (!supportsPush()) return () => {}
  const stopChanges = listenForSubscriptionChanges()
  if (!initialHealDone && Notification.permission === 'granted') {
    initialHealDone = true
    healWithRetry()
  }
  return stopChanges
}

export function useWebPush() {
  return {
    state,
    isOn: computed(() => state.status === 'on'),
    canEnable: computed(() => state.status === 'off' || state.status === 'failed'),
    load,
    enable,
    disable,
    saveTopics,
  }
}

export function resetWebPushForTests() {
  registeredEndpoint = null
  initialHealDone = false
  clearTimeout(healTimer)
  Object.assign(state, {
    status: 'loading',
    topics: [],
    availableTopics: [],
    busy: false,
    errorStatus: null,
  })
}
